import { useState } from 'react';
import { Box, Button } from '@ff-it/ui';
import { type ChainPath } from 'modules/supplier/supplier/components/ProductChainPicker';
import { actionErrorOrThrow } from 'utilities';
import type { Activity, ActivityType } from 'types';
import {
  DiscountTreeEditor,
  discountContextLabels as labels,
  discountPercentage as percentage,
  discountRuleLabel as ruleLabel,
  discountDiagnosticText as diagnosticText,
  indexOrderingDiagnostics,
  mediaTargetInvalidReason,
  type DiscountColumn,
  type DiscountTreeSection,
  reorderRuleIds,
} from 'modules/agreement/discountTree';
import type {
  ProviderAgreementDiscountRule,
  ProviderAgreementDiscountRuleInput,
  DiscountTreeNode,
  MutationResult,
  RuleMutation,
} from './types';
import { contexts } from './types';
import { RuleDialog } from './RuleDialog';
import * as styles from './ProviderAgreementDiscountEditor.css';
import { orderingDiagnostics, policyOrderingDiagnostics, type ProviderOrderingDiagnostic } from './orderingDiagnostics';
import { pathMatchesScope } from 'modules/supplier/supplier/components/ProductChainPicker';
import { formValuesFromRule } from './form';
import { FactorPolicyIndicator } from 'modules/agreement/factorPolicy/FactorPolicyIndicator';
import { policyDiagnosticText } from 'modules/agreement/factorPolicy/ordering';

export type ProviderAgreementDiscountEditorProps = {
  activeType: ActivityType;
  nodes: readonly DiscountTreeNode[];
  onAddRule?: RuleMutation;
  onEditRule?: (rule: ProviderAgreementDiscountRuleInput, ruleId: number) => Promise<MutationResult>;
  onRemoveRule?: (ruleId: number) => Promise<void>;
  onReorderRules?: (
    targetPath: ProviderAgreementDiscountRuleInput['target_path'],
    rateIds: readonly number[],
  ) => Promise<MutationResult>;
  targetOptions?: readonly ProviderAgreementDiscountRuleInput['target_path'][];
  /** Complete effective scope, never a candidate/search page. */
  scopePaths: readonly ChainPath[];
  activities?: readonly Activity[];
  readOnly?: boolean;
  disabled?: boolean;
};

export function ProviderAgreementDiscountEditor(props: ProviderAgreementDiscountEditorProps) {
  return <ProviderDiscountEditorContent key={props.activeType} {...props} />;
}

function ProviderDiscountEditorContent({
  activeType,
  nodes,
  onAddRule,
  onEditRule,
  onRemoveRule,
  onReorderRules,
  targetOptions = [],
  scopePaths,
  activities = [],
  readOnly = false,
  disabled = false,
}: ProviderAgreementDiscountEditorProps) {
  const [session, setSession] = useState<
    { mode: 'create' } | { mode: 'edit'; rule: ProviderAgreementDiscountRule } | null
  >(null);
  const close = () => setSession(null);
  const visibleContexts = contexts.filter((context) => context !== 'package_discount' || activeType === 'RADIO');
  const columns: DiscountColumn<ProviderAgreementDiscountRule>[] = visibleContexts.map((context) => ({
    id: context,
    header: labels[context],
    renderRule: (rule) => (
      <>
        {activeType === 'RADIO' && context === 'standard_discount' && (
          <FactorPolicyIndicator side="Provider" policy={rule.factor_policy} />
        )}
        {percentage(rule[context])}
      </>
    ),
  }));
  const diagnosticsByRule = indexOrderingDiagnostics<ProviderAgreementDiscountRule, ProviderOrderingDiagnostic>(
    nodes,
    orderingDiagnostics,
  );
  const policiesByRule = indexOrderingDiagnostics(nodes, policyOrderingDiagnostics);

  const sections: readonly DiscountTreeSection<ProviderAgreementDiscountRule>[] = [
    {
      id: 'main',
      includes: (_node, rule) =>
        mediaTargetInvalidReason(rule.target_path, activeType) == null &&
        pathMatchesScope(rule.target_path, scopePaths),
    },
    {
      id: 'attention',
      title: 'Needs attention',
      description: 'These rules have an invalid target or no longer match the current provided scope.',
      variant: 'attention',
      includes: (_node, rule) =>
        mediaTargetInvalidReason(rule.target_path, activeType) != null ||
        !pathMatchesScope(rule.target_path, scopePaths),
    },
  ];

  const renderDialog = (selected: NonNullable<typeof session>) => {
    const editingRuleId = selected.mode === 'edit' ? selected.rule.id : undefined;
    const draft =
      selected.mode === 'edit'
        ? formValuesFromRule(selected.rule)
        : {
            target_path: null,
            terms: '',
            target_category: null,
            target_activity: null,
            standard_discount: null,
            non_standard_discount: null,
            package_discount: null,
            factor_policy: null,
          };
    return (
      <RuleDialog
        open
        activeType={activeType}
        draft={draft}
        editingRuleId={editingRuleId}
        activities={activities}
        targetOptions={targetOptions}
        scopePaths={scopePaths}
        onSubmit={async (rule) => {
          const result =
            editingRuleId !== undefined ? await onEditRule?.(rule, editingRuleId) : await onAddRule?.(rule);
          if (result) return result;
          close();
        }}
        onRemove={
          editingRuleId !== undefined && onRemoveRule
            ? async () => {
                await onRemoveRule(editingRuleId);
                close();
              }
            : undefined
        }
        onClose={close}
      />
    );
  };
  const reorder = async (
    node: DiscountTreeNode,
    source: ProviderAgreementDiscountRule,
    target: ProviderAgreementDiscountRule,
    edge: 'top' | 'bottom',
  ) => {
    if (!onReorderRules) return;
    try {
      await onReorderRules(source.target_path, reorderRuleIds(node.rules, source.id, target.id, edge));
    } catch (error) {
      actionErrorOrThrow(error as never);
    }
  };

  return (
    <Box minWidth="0" data-testid="provider-agreement-discount-editor">
      <section className={styles.panel} aria-label="Discount tree">
        <DiscountTreeEditor<ProviderAgreementDiscountRule>
          key={activeType}
          nodes={nodes}
          columns={columns}
          presentRule={(rule) => ({
            label: ruleLabel(rule),
            ariaLabel: ruleLabel(rule),
            description: rule.terms,
            diagnostic: (
              <>
                {diagnosticText(diagnosticsByRule.get(rule.id) ?? [], visibleContexts)}
                {activeType === 'RADIO' && policyDiagnosticText(policiesByRule.get(rule.id) ?? [])}
              </>
            ),
          })}
          sections={sections}
          emptyText="No provider discount rules yet."
          readOnly={readOnly}
          disabled={disabled}
          onEdit={onEditRule ? (_node, rule) => setSession({ mode: 'edit', rule }) : undefined}
          createAction={
            onAddRule && (
              <Button
                variant="secondary"
                size="sm"
                icon="plus"
                disabled={!targetOptions.length}
                onClick={() => setSession({ mode: 'create' })}
              >
                Add rule
              </Button>
            )
          }
          onReorder={onReorderRules ? reorder : undefined}
        />
      </section>
      {!readOnly && !disabled && session && renderDialog(session)}
    </Box>
  );
}
